import type { OAuth2Config, OAuth2FormValues } from '#layers/access/utils/auth-providers/protocols/oauth2';
import type { ValueOf } from '#layers/base/public';
import type { FormSchemaResolved } from '#layers/block-engine/public';
import { BuiltinBlockIds } from 'virtual:monospace-builtin-block-ids';
import { AuthProtocol, defineAuthProvider } from '#layers/access/types/auth-providers/define';
import { oauth2 } from '#layers/access/utils/auth-providers/protocols/oauth2';
import {
	applyOAuth2PresetOptions,
	normalizeDomain,
	OIDC_CLAIM_MAPPINGS,
	OIDC_SCOPES,
	promoteOAuth2PresetFieldsBeforeCredentials,
} from './oauth2-preset';

const MICROSOFT_ENTRA_AUTHORITY = {
	single: 'single',
	organizations: 'organizations',
	common: 'common',
} as const;

type MicrosoftEntraAuthority = ValueOf<typeof MICROSOFT_ENTRA_AUTHORITY>;

interface MicrosoftEntraFormValues extends Omit<OAuth2FormValues, 'authorizationEndpoint' | 'tokenEndpoint' | 'userinfoEndpoint'> {
	authority: MicrosoftEntraAuthority;
	tenant?: string;
}

const MICROSOFT_ENTRA_DEFAULTS = {
	'scopes': OIDC_SCOPES,
	'claimMappings.sub': OIDC_CLAIM_MAPPINGS.sub,
	'claimMappings.email': OIDC_CLAIM_MAPPINGS.email,
} as const;

const MICROSOFT_ENTRA_REGISTER_APP_URL = 'https://learn.microsoft.com/en-us/entra/identity-platform/quickstart-register-app';

const MICROSOFT_ENTRA_DESCRIPTIONS: Record<string, string> = {
	authority: '$t:sso_providers.microsoft_entra.authority_hint',
};

function microsoftEntraEndpoints(authorityInput: MicrosoftEntraAuthority, tenantInput?: string): Pick<OAuth2Config, 'authorizationEndpoint' | 'tokenEndpoint' | 'userinfoEndpoint'> {
	const authority = authorityInput === MICROSOFT_ENTRA_AUTHORITY.single
		? normalizeDomain(tenantInput ?? '')
		: authorityInput;

	if (!authority) {
		return {
			authorizationEndpoint: '',
			tokenEndpoint: '',
			userinfoEndpoint: 'https://graph.microsoft.com/oidc/userinfo',
		};
	}

	return {
		authorizationEndpoint: `https://login.microsoftonline.com/${authority}/oauth2/v2.0/authorize`,
		tokenEndpoint: `https://login.microsoftonline.com/${authority}/oauth2/v2.0/token`,
		userinfoEndpoint: 'https://graph.microsoft.com/oidc/userinfo',
	};
}

function parseMicrosoftEntraInitialValues(config: unknown): Partial<MicrosoftEntraFormValues> {
	const {
		authorizationEndpoint: _authorizationEndpoint,
		tokenEndpoint: _tokenEndpoint,
		userinfoEndpoint: _userinfoEndpoint,
		...parsed
	} = oauth2.parseInitialValues?.(config as OAuth2Config) ?? {};
	const authEndpoint = (config as { authorizationEndpoint?: unknown })?.authorizationEndpoint;

	if (typeof authEndpoint !== 'string') return parsed;

	let segment: string | undefined;

	try {
		segment = new URL(authEndpoint).pathname.split('/').find(Boolean);
	}
	catch {
		// Malformed URL in saved config — fall back to base parse so the form
		// renders the raw value rather than crashing on edit.
		return parsed;
	}

	const authority = segment ?? MICROSOFT_ENTRA_AUTHORITY.single;

	if (authority === MICROSOFT_ENTRA_AUTHORITY.organizations || authority === MICROSOFT_ENTRA_AUTHORITY.common) {
		return {
			...parsed,
			authority,
		};
	}

	return {
		...parsed,
		authority: MICROSOFT_ENTRA_AUTHORITY.single,
		tenant: authority,
	};
}

function applyMicrosoftEntraDefaults(items: FormSchemaResolved<any>, values: Partial<MicrosoftEntraFormValues>): FormSchemaResolved<any> {
	const configured = applyOAuth2PresetOptions(items, {
		defaults: MICROSOFT_ENTRA_DEFAULTS,
		descriptions: MICROSOFT_ENTRA_DESCRIPTIONS,
		fieldOverrides: {
			authorizationEndpoint: {
				default: () => microsoftEntraEndpoints(values.authority ?? MICROSOFT_ENTRA_AUTHORITY.single, values.tenant).authorizationEndpoint || undefined,
				props: { type: 'url', readonly: true },
			},
			tokenEndpoint: {
				default: () => microsoftEntraEndpoints(values.authority ?? MICROSOFT_ENTRA_AUTHORITY.single, values.tenant).tokenEndpoint || undefined,
				props: { type: 'url', readonly: true },
			},
			userinfoEndpoint: {
				default: () => microsoftEntraEndpoints(values.authority ?? MICROSOFT_ENTRA_AUTHORITY.single, values.tenant).userinfoEndpoint,
				props: { type: 'url', readonly: true },
			},
		},
	});

	return promoteOAuth2PresetFieldsBeforeCredentials([
		...configured,
		{
			name: 'authority',
			label: '$t:sso_providers.microsoft_entra.authority_label',
			description: '$t:sso_providers.microsoft_entra.authority_hint',
			block: BuiltinBlockIds.InputTileRadio,
			required: true,
			default: MICROSOFT_ENTRA_AUTHORITY.single,
			props: {
				options: [
					{
						value: MICROSOFT_ENTRA_AUTHORITY.single,
						label: '$t:sso_providers.microsoft_entra.authority_single_label',
						description: '$t:sso_providers.microsoft_entra.authority_single_description',
						icon: 'lucide:building-2',
					},
					{
						value: MICROSOFT_ENTRA_AUTHORITY.organizations,
						label: '$t:sso_providers.microsoft_entra.authority_organizations_label',
						description: '$t:sso_providers.microsoft_entra.authority_organizations_description',
						icon: 'lucide:network',
					},
					{
						value: MICROSOFT_ENTRA_AUTHORITY.common,
						label: '$t:sso_providers.microsoft_entra.authority_common_label',
						description: '$t:sso_providers.microsoft_entra.authority_common_description',
						icon: 'lucide:users',
					},
				],
				autoTitleCaseValue: false,
			},
		},
		{
			name: 'tenant',
			label: '$t:sso_providers.microsoft_entra.tenant_label',
			block: BuiltinBlockIds.Input,
			required: true,
			hidden: () => values.authority !== MICROSOFT_ENTRA_AUTHORITY.single,
			default: '',
			props: { placeholder: '$t:sso_providers.microsoft_entra.tenant_placeholder' },
		},
	], ['authority', 'tenant']);
}

export const microsoftEntra = defineAuthProvider<typeof oauth2, OAuth2Config, MicrosoftEntraFormValues>({
	id: 'microsoft-entra',
	name: '$t:sso_providers.microsoft_entra.name',
	description: '$t:sso_providers.microsoft_entra.description',
	protocol: AuthProtocol.OAuth2,
	icon: 'logos:microsoft-icon',
	docsUrl: MICROSOFT_ENTRA_REGISTER_APP_URL,
	walkthroughStepKeys: [
		{
			key: 'sso_providers.microsoft_entra.walkthrough.register_app',
			href: MICROSOFT_ENTRA_REGISTER_APP_URL,
		},
		{ key: 'sso_providers.microsoft_entra.walkthrough.redirect', showCallbackUrl: true },
		'sso_providers.microsoft_entra.walkthrough.secret',
		'sso_providers.microsoft_entra.walkthrough.credentials',
	],
	extends: oauth2,
	options: ({ form, context, values }) =>
		form(applyMicrosoftEntraDefaults(context.base?.resolvedOptions ?? [], values) as never),
	transformOptions: (form): OAuth2Config => {
		const { authority: _authority, tenant: _tenant, ...rest } = form;

		return oauth2.transformOptions!({
			...rest,
			...microsoftEntraEndpoints(form.authority, form.tenant),
		});
	},
	parseInitialValues: parseMicrosoftEntraInitialValues,
});
